import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMyAvailability, saveMyAvailability } from '../../lib/shiftApi';
import { useShiftI18n } from '../../i18n/ShiftI18nContext';
import {
  TimeFields, rangeOverrides, allDayBase, cellLabel, snapTime,
  sameFill, describeFill, DEFAULT_TIME_BAR,
} from './availTimeFields';
import './shift.css';

function currentMonthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function cloneEntries(entries) {
  return JSON.parse(JSON.stringify(entries || []));
}

function entryMap(entries) {
  const m = {};
  for (const e of entries || []) m[e.day] = e;
  return m;
}

function ensureDay(map, day, fillMode) {
  if (!map[day]) {
    map[day] = fillMode === 'cannot'
      ? { day, base: '可', overrides: [], note: '' }
      : { day, base: null, overrides: [], note: '' };
  }
  return map[day];
}

function isFilled(e) {
  return !!(e && (e.base === '可' || e.base === '休' || (e.overrides && e.overrides.length)));
}

const WD_ORDER = ['日', '一', '二', '三', '四', '五', '六'];

export default function ShiftAvailability() {
  const nav = useNavigate();
  const { t } = useShiftI18n();
  const [monthOffset, setMonthOffset] = useState(0);
  const month = useMemo(() => currentMonthKey(monthOffset), [monthOffset]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [meta, setMeta] = useState(null);
  const [entries, setEntries] = useState([]);
  const [savedSnap, setSavedSnap] = useState('[]');
  const [weekdays, setWeekdays] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [copyDay, setCopyDay] = useState(null);
  const [path, setPath] = useState('');
  const [timeKind, setTimeKind] = useState('range');
  const [rangeFrom, setRangeFrom] = useState(12);
  const [rangeTo, setRangeTo] = useState(17);
  const [aroundTime, setAroundTime] = useState(21);
  const [aroundDir, setAroundDir] = useState('後');
  const [hydratedBase, setHydratedBase] = useState(null);

  const dirty = useMemo(() => JSON.stringify(entries) !== savedSnap, [entries, savedSnap]);
  const missingCount = useMemo(() => {
    if (!meta) return 0;
    const map = entryMap(entries);
    let n = 0;
    for (const w of weekdays) {
      if (!isFilled(map[w.day])) n += 1;
    }
    return n;
  }, [entries, weekdays, meta]);

  const confirmLeave = useCallback(() => {
    if (!dirty) return true;
    return window.confirm('有未儲存的變更，確定離開？');
  }, [dirty]);

  const pushHistory = useCallback((next) => {
    setUndoStack((s) => [...s.slice(-40), cloneEntries(entries)]);
    setRedoStack([]);
    setEntries(next);
  }, [entries]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetchMyAvailability(month);
      if (!r?.ok) {
        setErr(r?.error === 'meta_missing' ? '尚未綁定月排班身分，請聯繫管理者' : (r?.message || r?.error || '載入失敗'));
        setMeta(null);
        return;
      }
      setMeta(r);
      setEntries(cloneEntries(r.entries || []));
      setSavedSnap(JSON.stringify(r.entries || []));
      setWeekdays(r.weekdays || []);
      setIsLocked(!r.canEdit);
      setUndoStack([]);
      setRedoStack([]);
      setSelected(new Set());
      setCopyDay(null);
      setPath('');
    } catch {
      setErr('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected.size) {
      setPath(DEFAULT_TIME_BAR.path);
      setTimeKind(DEFAULT_TIME_BAR.timeKind);
      setRangeFrom(DEFAULT_TIME_BAR.rangeFrom);
      setRangeTo(DEFAULT_TIME_BAR.rangeTo);
      setAroundTime(DEFAULT_TIME_BAR.aroundTime);
      setAroundDir(DEFAULT_TIME_BAR.aroundDir);
      setHydratedBase(null);
      return;
    }
    const map = entryMap(entries);
    const { same, sample } = sameFill([...selected].map((d) => map[d]));
    const next = same ? describeFill(sample) : { ...DEFAULT_TIME_BAR };
    setPath(next.path);
    setTimeKind(next.timeKind);
    setRangeFrom(next.rangeFrom);
    setRangeTo(next.rangeTo);
    setAroundTime(next.aroundTime);
    setAroundDir(next.aroundDir);
    setHydratedBase(next.hydratedBase);
  }, [selected, entries]);

  const writeDays = (days, mutator) => {
    if (isLocked || !days.length) return false;
    const map = entryMap(entries);
    for (const day of days) {
      const e = ensureDay(map, day, meta?.fillMode);
      mutator(e);
    }
    pushHistory(Object.values(map).sort((a, b) => a.day - b.day));
    return true;
  };

  const closePanel = () => {
    setSelected(new Set());
    setPath('');
  };

  const applyBaseDays = (days, val) => {
    if (!writeDays(days, (e) => { e.base = val; e.overrides = []; })) return;
    setToast(`已套用 ${days.length} 天`);
    closePanel();
  };

  const applyTimeDays = (days) => {
    const mutator = timeKind === 'range'
      ? (e) => { e.base = null; e.overrides = rangeOverrides(rangeFrom, rangeTo); }
      : (e) => { e.base = null; e.overrides = [{ time: snapTime(aroundTime), dir: aroundDir, state: '可' }]; };
    if (!writeDays(days, mutator)) return;
    setToast(`已套用 ${days.length} 天`);
    closePanel();
  };

  const fillBlank = (val) => {
    if (isLocked) return;
    const map = entryMap(entries);
    let n = 0;
    for (const w of weekdays) {
      if (isFilled(map[w.day])) continue;
      map[w.day] = { day: w.day, base: val, overrides: [], note: map[w.day]?.note || '' };
      n += 1;
    }
    if (!n) { setToast('沒有未填的日子'); return; }
    pushHistory(Object.values(map).sort((a, b) => a.day - b.day));
    setToast(`已填 ${n} 天`);
  };

  const toggleSelect = (day) => {
    if (isLocked) return;
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(day)) n.delete(day);
      else n.add(day);
      return n;
    });
  };

  const pasteToSelected = () => {
    if (isLocked || copyDay == null || !selected.size) return;
    const src = entryMap(entries)[copyDay] || { day: copyDay, base: null, overrides: [], note: '' };
    const map = entryMap(entries);
    let n = 0;
    for (const day of selected) {
      if (day === copyDay) continue;
      map[day] = {
        day,
        base: src.base ?? null,
        overrides: cloneEntries(src.overrides || []),
        note: src.note || '',
      };
      n += 1;
    }
    if (!n) { setToast('請先點要貼上的日子'); return; }
    pushHistory(Object.values(map).sort((a, b) => a.day - b.day));
    setToast(`已貼上 ${n} 天`);
    closePanel();
    setCopyDay(null);
  };

  const undo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((r) => [...r, cloneEntries(entries)]);
    setUndoStack((s) => s.slice(0, -1));
    setEntries(prev);
  };

  const redo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((s) => [...s, cloneEntries(entries)]);
    setRedoStack((r) => r.slice(0, -1));
    setEntries(next);
  };

  const save = async () => {
    if (isLocked || saving) return;
    setSaving(true); setErr('');
    try {
      const payload = entries.map((e) => {
        const o = { day: e.day };
        if (e.overrides?.length) o.overrides = e.overrides;
        else if (e.base) o.base = e.base;
        if (e.note) o.note = e.note;
        return o;
      });
      const r = await saveMyAvailability({ month, entries: payload });
      if (!r?.ok) {
        if (r?.error === 'incomplete') setErr(`還有 ${r.missingDays?.length || ''} 天未設定`);
        else if (r?.error === 'fill_window_closed' || r?.error === 'month_locked') setErr(r.message || '請跟老闆說，由老闆調整');
        return;
      }
      setSavedSnap(JSON.stringify(entries));
      setToast('已儲存');
    } catch {
      setErr('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const map = entryMap(entries);
  const selectedN = selected.size;
  const pad = weekdays[0] ? Math.max(0, WD_ORDER.indexOf(weekdays[0].wd)) : 0;
  const timeProps = {
    kind: timeKind,
    onKind: setTimeKind,
    rangeFrom,
    rangeTo,
    onRange: (a, b) => { setRangeFrom(a); setRangeTo(b); },
    aroundTime,
    aroundDir,
    onAround: (tm, d) => { setAroundTime(tm); setAroundDir(d); },
    disabled: isLocked,
  };

  return (
    <>
      <div className="shift-topbar shift-topbar-compact">
        <div className="shift-tb-row">
          <button
            type="button"
            className="shift-back"
            onClick={() => { if (!confirmLeave()) return; nav('/shift/home'); }}
          >
            ←
          </button>
          <div className="shift-tb-info">
            <div className="shift-tb-title">{t('avail.title')}</div>
            {meta?.person ? <div className="shift-tb-sub">{meta.person}</div> : null}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="avail-icon-btn" disabled={!undoStack.length} onClick={undo}>↶</button>
            <button type="button" className="avail-icon-btn" disabled={!redoStack.length} onClick={redo}>↷</button>
          </div>
        </div>
        <div className="shift-tb-row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="avail-month-btn" disabled={monthOffset <= 0} onClick={() => { if (!confirmLeave()) return; setMonthOffset((v) => v - 1); }}>{t('month.prev')}</button>
          <strong>{month}</strong>
          <button type="button" className="avail-month-btn" disabled={monthOffset >= 1} onClick={() => { if (!confirmLeave()) return; setMonthOffset((v) => v + 1); }}>{t('month.next')}</button>
        </div>
        {!loading && meta && meta.fillWindow && !isLocked && (
          <div className="avail-window-line">
            填寫區間：{meta.fillWindow.openAt ? new Date(meta.fillWindow.openAt).toLocaleString('zh-TW') : '—'}
            {' '}～{' '}
            {meta.fillWindow.closeAt ? new Date(meta.fillWindow.closeAt).toLocaleString('zh-TW') : '—'}
          </div>
        )}
      </div>

      <div className="shift-scroll">
        {loading && <div className="shift-empty">{t('common.loading')}</div>}
        {err && <div className="shift-login-err" style={{ margin: 12 }}>{err}</div>}
        {isLocked && <div className="avail-lock">{meta?.fillWindow?.message || '本月已鎖定，僅可查看'}</div>}
        {copyDay != null && (
          <div className="avail-copy-banner">
            <span>已複製 {copyDay} 日 · 點其他日再貼上</span>
            <button type="button" onClick={() => setCopyDay(null)}>取消</button>
          </div>
        )}
        {!loading && meta && (
          <>
            {missingCount > 0 && (
              <div className="avail-warn">
                還有 {missingCount} 天未填
                {!isLocked && (
                  <span className="avail-warn-actions">
                    <button type="button" onClick={() => fillBlank('可')}>未填的都可</button>
                    <button type="button" onClick={() => fillBlank('休')}>未填的都休</button>
                  </span>
                )}
              </div>
            )}
            <div className="avail-hint">點日期複選，再按可／休／自訂</div>
            <div className="avail-cal-wd">
              {WD_ORDER.map((w) => <div key={w}>{w}</div>)}
            </div>
            <div className="avail-cal">
              {Array.from({ length: pad }, (_, i) => <div key={`p${i}`} className="avail-cell empty" />)}
              {weekdays.map((w) => {
                const e = map[w.day] || { day: w.day, base: null, overrides: [], note: '' };
                const label = cellLabel(e);
                const invalid = !isFilled(e);
                const checked = selected.has(w.day);
                const base = allDayBase(e);
                return (
                  <div
                    key={w.day}
                    className={`avail-cell ${w.weekend ? 'weekend' : ''} ${invalid ? 'invalid' : ''} ${checked ? 'checked' : ''} ${copyDay === w.day ? 'copy-src' : ''} ${base === '可' ? 'ok' : ''} ${base === '休' ? 'off' : ''} ${isLocked ? 'locked' : ''}`}
                    role="button"
                    tabIndex={isLocked ? -1 : 0}
                    onClick={() => toggleSelect(w.day)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        toggleSelect(w.day);
                      }
                    }}
                  >
                    <span className="avail-cell-num">{w.day}</span>
                    <span className="avail-cell-st">{label || (invalid ? '未填' : '')}</span>
                    {e.note ? <span className="avail-cell-note">·</span> : null}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {!loading && meta && selectedN > 0 && (
        <div className="avail-batch">
          <div className="avail-batch-hd">
            <strong>已選 {selectedN} 天</strong>
            <button type="button" className="avail-month-btn" onClick={closePanel}>取消</button>
          </div>
          {path !== 'custom' ? (
            <div className="avail-seg">
              <button
                type="button"
                className={`avail-seg-btn on ok${hydratedBase === '可' ? ' picked' : ''}`}
                disabled={isLocked}
                onClick={() => applyBaseDays([...selected], '可')}
              >
                可
              </button>
              <button
                type="button"
                className={`avail-seg-btn on off${hydratedBase === '休' ? ' picked' : ''}`}
                disabled={isLocked}
                onClick={() => applyBaseDays([...selected], '休')}
              >
                休
              </button>
              <button
                type="button"
                className="avail-seg-btn"
                disabled={isLocked}
                onClick={() => setPath('custom')}
              >
                自訂
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="avail-text-btn" style={{ marginTop: 0 }} onClick={() => setPath('')}>
                ← 改回可／休
              </button>
              <TimeFields {...timeProps} />
              <button type="button" className="avail-apply-btn" disabled={isLocked} onClick={() => applyTimeDays([...selected])}>
                套用時段到這 {selectedN} 天
              </button>
              {selectedN === 1 && !isLocked && (
                <button
                  type="button"
                  className="avail-text-btn"
                  onClick={() => {
                    const day = [...selected][0];
                    setCopyDay(day);
                    setToast(`已複製 ${day} 日，點其他日再貼上`);
                  }}
                >
                  複製這天
                </button>
              )}
            </>
          )}
          {copyDay != null && (
            <button type="button" className="avail-apply-btn avail-paste-btn" disabled={isLocked} onClick={pasteToSelected}>
              貼到這 {selectedN} 天
            </button>
          )}
        </div>
      )}

      {!loading && meta && (
        <div className="avail-sticky">
          <span>{dirty ? '未存變更' : '已同步'}{missingCount ? ` · 缺 ${missingCount} 天` : ''}</span>
          <button
            type="button"
            className={`shift-login-btn ${dirty ? 'avail-save-dirty' : ''}`}
            style={{ marginTop: 0, width: 'auto', minWidth: 120 }}
            disabled={isLocked || saving || !dirty || missingCount > 0}
            onClick={save}
          >
            {saving ? '儲存中…' : t('common.save')}
          </button>
        </div>
      )}
      {toast && <div className="avail-toast">{toast}</div>}
    </>
  );
}
